import React, { useLayoutEffect, useRef, useState } from "react";
import { Button, Form, Radio as AntRadio } from "antd";
import { Formik, FormikProps } from "formik";
import { throttle } from "lodash";

import * as values from "values";
import { OfferTypeNames } from "values";
import { prepareParams, searchOffersCount } from "Api/api";
import { getPathFromFilter } from "Helpers/path";
import Radio from "Form/Radio";
import OnChangeHandler from "Form/OnChangeHandler";
import TypesDropdown from "Components/Filters/TypesDropdown";
import { formItemLayout } from "Form/formLayout";
import { useOfferTypeState } from "ContextStore";
import { AllFilterTypes, AnyFilterTypes, AnyOfferTypes, TypedComponentProps } from "Types";

type PropsType<T> = TypedComponentProps & {
    label: OfferTypeNames;
    children: (formik: FormikProps<T>, filter: AnyFilterTypes) => JSX.Element;
};
const FilterComponent = <TOffer extends AnyOfferTypes, TFilter extends AnyFilterTypes>({
    type,
    label,
    children,
}: PropsType<TFilter>): JSX.Element => {
    const [resultsCount, setResultsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const { filter } = useOfferTypeState<TOffer[], TFilter>(type);
    const ref = useRef<HTMLDivElement>(null);

    const onSubmit = (data: TFilter, { setSubmitting }: { setSubmitting: (value: boolean) => void }): void => {
        setSubmitting(false);
        const preparedParams = prepareParams(data);
        const paramsArray = Object.entries(preparedParams)
            .map((value) => `${value[0]}=${value[1]}`)
            .join("::");
        window.location.assign(
            window.rmxo_indexPage + getPathFromFilter(data) + "/?params=" + btoa(paramsArray) + "&true"
        );
    };

    const handleOnChange = (values: AllFilterTypes): void => {
        searchOffersCount(values)
            .then(setResultsCount)
            .then(() => setIsLoading(false));
    };

    return (
        <section className={"rmxo-filter rmxo-container"} ref={ref}>
            <TypesDropdown selectedLabel={label} />
            <Formik initialValues={{ ...filter }} enableReinitialize={true} onSubmit={onSubmit} {...formItemLayout}>
                {(formik): JSX.Element => (
                    <Form onFinish={(): Promise<void> => formik.submitForm()}>
                        <Form.Item>
                            <AntRadio.Group buttonStyle={"solid"} className={"rmxo-radio-group"}>
                                {values.availableOfferTypes.map((type) => (
                                    <Radio
                                        name={"offerType"}
                                        setValue={formik.setFieldValue}
                                        key={type.id}
                                        value={type.id}
                                        label={type.value}
                                    />
                                ))}
                            </AntRadio.Group>
                        </Form.Item>
                        {children(formik, filter)}
                        <StickyBar parentRef={ref}>
                            <Form.Item>
                                {!isLoading && resultsCount === 0 && (
                                    <div className={"rmxo-centered-text"}>
                                        <p>
                                            <strong>
                                                Dle Vámi vybraných kritérií nebyla nalezena žádná vyhovující nemovitost.
                                            </strong>
                                        </p>
                                        <p>Zkuste upravit Váš filtr tak, aby zahrnoval více nemovitostí.</p>
                                    </div>
                                )}
                                {(isLoading || resultsCount > 0) && (
                                    <Button
                                        type={"primary"}
                                        htmlType="submit"
                                        className={"rmxo-button-centered"}
                                        loading={isLoading}
                                    >
                                        Zobrazit {resultsCount > 0 ? `${resultsCount} nemovitostí` : "nemovitosti"}
                                    </Button>
                                )}
                                <OnChangeHandler handleLoading={setIsLoading} handleChange={handleOnChange} />
                            </Form.Item>
                        </StickyBar>
                    </Form>
                )}
            </Formik>
        </section>
    );
};

const StickyBar: React.FC<{ parentRef: React.RefObject<HTMLDivElement> }> = ({ parentRef, children }) => {
    const [isSticky, _setSticky] = useState(false);
    const divRef = useRef<HTMLDivElement>(null);
    const isStickyRef = useRef(isSticky);

    const setSticky = (value: boolean) => {
        isStickyRef.current = value; // keep updated
        _setSticky(value);
    };

    const handleScrolling = throttle(() => {
        if (parentRef.current && divRef.current) {
            const shouldStick =
                parentRef.current.getBoundingClientRect().bottom +
                    (isStickyRef.current ? divRef.current.getBoundingClientRect().height : 0) +
                    250 >
                window.scrollY;
            if (shouldStick !== isStickyRef.current) {
                setSticky(shouldStick);
            }
        }
    }, 250);

    useLayoutEffect(() => {
        window.addEventListener("scroll", handleScrolling);
        return () => window.removeEventListener("scroll", handleScrolling);
    }, []);

    return (
        <>
            <div className={`rmxo-sticky-bar ${isSticky ? "sticky" : ""}`} ref={divRef}>
                {children}
            </div>
            {isSticky && (
                <div className={`rmxo-sticky-bar`} style={{ visibility: "hidden" }}>
                    {children}
                </div>
            )}
        </>
    );
};

export default FilterComponent;
